import { findOrCreateJob } from '@/api/extension';
import { getAtsParams, vueGet } from '@/api/vue';
import { useAppRoute } from '@/stores/app/route';
import { useDocumentEditor } from '@/stores/document/editor';
import { v4 as uuidv4 } from 'uuid';
import { ref, computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';

export const useDatapeopleAnywhereEditor = defineStore('datapeopleAnywhereEditor', () => {
  const jobId = ref(null);
  const revisionId = ref(null);
  const atsJobId = ref(null);
  const atsPostId = ref(null);

  const state = {
    jobId, revisionId, atsJobId, atsPostId,
  };

  const isDatapeopleAnywhereEditor = computed(() => {
    const { name, params } = useAppRoute().route;
    return name === 'editor-view' && params.id === 'datapeople-anywhere';
  });

  const updateIds = (params) => {
    Object.keys(params)
      .filter((key) => params[key] !== undefined)
      .forEach((key) => {
        state[key].value = params[key];
      });
  };

  const fields = ref([]);
  const templateList = ref([]);
  const initData = ref({});
  const vueGetData = ref({});

  const loadInitialData = async (atsId) => {
    const atsParams = await getAtsParams(atsId);
    fields.value = atsParams.fields;
    templateList.value = atsParams.templateList;

    initData.value = {
      success: true,
      body: {
        jobId: 'datapeople-anywhere',
        documentType: 'job',
        isEditable: true,
        templateList,
      },
    };

    vueGetData.value = {
      success: true,
      id: 0,
      title: '',
      company: '',
      location: '',
      description_json: {
        fields: fields.value.map((field) => ({
          ...field,
          id: uuidv4(),
          sections: [{
            type: 'default',
            id: uuidv4(),
            formatBlacklist: [],
            blocks: [{
              id: uuidv4(),
              formatBlacklist: [],
              type: 'paragraph',
              html: '<p></p>',
            }],
          }],
        })),
      },
      description_delta: null,
      bodyMessages: {},
      allRevisions: [{
        id: 0,
      }],
    };

    return [initData.value, vueGetData.value];
  };

  const currentRevision = computed(() => {
    const {
      title, company, location, flexibleId, fieldsOriginal,
    } = storeToRefs(useDocumentEditor());

    return {
      id: revisionId.value,
      title: title.value,
      company: company.value,
      location: location.value,
      flexible_id: flexibleId.value,
      description_json: { fields: fieldsOriginal.value },
    };
  });

  const findOrCreateDatapeoleAnywhereJob = async () => {
    if (jobId.value) {
      return { id: jobId.value, revision: revisionId.value };
    }

    const { id, revisionId: newRevisionId } = await findOrCreateJob({
      atsPostId: atsPostId.value,
      atsJobId: atsJobId.value,
      job: currentRevision.value,
    });

    jobId.value = id;
    revisionId.value = newRevisionId;

    return { id, revisionId: newRevisionId };
  };

  const previousRevision = ref({});

  const showDatapeopleAnywhereSideBySide = ref(false);

  const applyTemplateToDatapeoleAnywhereJob = async ({ template, hasContent }) => {
    const templateData = await vueGet({ id: template });
    previousRevision.value = JSON.parse(JSON.stringify(currentRevision.value));
    useDocumentEditor().updateDocumentFromTemplate(templateData);

    if (hasContent) {
      showDatapeopleAnywhereSideBySide.value = true;
    }
  };

  const acceptSideBySide = () => {
    showDatapeopleAnywhereSideBySide.value = false;
  };

  const revertSideBySide = () => {
    useDocumentEditor().updateDocumentFromTemplate(previousRevision.value);
    showDatapeopleAnywhereSideBySide.value = false;
  };

  const similarJobId = ref(null);

  const setSimilarJobId = (id) => {
    similarJobId.value = Number(id);
  };

  return {
    jobId,
    revisionId,
    atsJobId,
    atsPostId,
    isDatapeopleAnywhereEditor,
    showDatapeopleAnywhereSideBySide,
    previousRevision,
    loadInitialData,
    updateIds,
    findOrCreateDatapeoleAnywhereJob,
    applyTemplateToDatapeoleAnywhereJob,
    acceptSideBySide,
    revertSideBySide,
    similarJobId,
    setSimilarJobId,
  };
});
